import { z } from "zod";
import {
  COUNTRIES,
  ENGAGEMENT_WAYS,
  EXPERIENCE_TYPES,
  GENDER_OPTIONS,
  GUIDANCE_TYPES,
  HEAR_ABOUT_US,
  MARKETS,
  MENTEE_PREFERENCES,
  MENTOR_FREQUENCY,
  MENTOR_HOURS,
  MENTOR_MODELS,
  PAID_OR_VOLUNTEER,
  STUDY_LEVELS,
  SUPPORT_AREA,
  SUPPORT_MODES,
  YEARS_OF_EXPERIENCE,
  YES_NO,
} from "../data/options";

// One schema for all three tracks. Track-specific fields are optional in the
// base shape; superRefine enforces them for the track(s) that need them.
// Advisor questions are the original advisors-call set, unchanged. Mentor
// questions are Haneen's, verbatim.

export const TRACKS = ["advisor", "mentor", "both"] as const;
export type Track = (typeof TRACKS)[number];

const cvFileSchema = z.object({
  name: z.string().min(1),
  type: z.string(),
  size: z.number().max(4 * 1024 * 1024, "CV must be 4 MB or smaller."),
  base64: z.string().min(1),
});

const ratingSchema = z.number().int().min(1).max(5).optional();

export const applicationSchema = z
  .object({
    track: z.enum(TRACKS),
    safeguardingAgree: z.literal(true, {
      errorMap: () => ({
        message: "You must agree to the Mercy Corps Safeguarding Policy.",
      }),
    }),

    // About you (all tracks)
    fullName: z.string().trim().min(2, "Please enter your full name."),
    gender: z.enum(GENDER_OPTIONS, {
      errorMap: () => ({ message: "Please select an option." }),
    }),
    country: z.enum(COUNTRIES, {
      errorMap: () => ({ message: "Please select your country." }),
    }),
    email: z.string().trim().email("Please enter a valid email."),
    whatsapp: z
      .string()
      .trim()
      .min(6, "Please enter a valid WhatsApp number.")
      .max(32),
    linkedin: z
      .string()
      .trim()
      .url("LinkedIn URL must start with https://")
      .refine((u) => /linkedin\.com/i.test(u), "Must be a LinkedIn profile URL."),

    // Advisor track (original questions; required when track is advisor/both)
    ratingTechIndustry: ratingSchema,
    ratingPalestinianEcosystem: ratingSchema,
    experienceTypes: z.array(z.enum(EXPERIENCE_TYPES)).default([]),
    experienceTypeDetails: z.string().trim().max(1000).optional().default(""),
    cLevelExperience: z.enum(YES_NO).optional(),
    cLevelDetails: z.string().trim().max(1000).optional().default(""),
    currentPosition: z.string().trim().max(200).optional().default(""),
    currentEmployer: z.string().trim().max(200).optional().default(""),
    yearsOfExperience: z.enum(YEARS_OF_EXPERIENCE).optional(),
    supportArea: z.array(z.enum(SUPPORT_AREA)).default([]),
    nonTechSubjects: z.array(z.string().trim()).default([]),
    nonTechSubjectsOther: z.string().trim().max(500).optional().default(""),
    techSpecializations: z.array(z.string().trim()).default([]),
    techSpecializationsOther: z.string().trim().max(500).optional().default(""),
    workedWithGsgBefore: z.enum(YES_NO).optional(),
    paidOrVolunteer: z.enum(PAID_OR_VOLUNTEER).optional(),
    hourlyRateUsd: z.string().trim().max(64).optional().default(""),
    markets: z.array(z.enum(MARKETS)).default([]),
    marketsExperience: z.string().trim().max(2000).optional().default(""),
    engagementWays: z.array(z.enum(ENGAGEMENT_WAYS)).default([]),
    supportModes: z.array(z.enum(SUPPORT_MODES)).default([]),

    // Mentor track (Haneen's questions; required when track is mentor/both)
    isUniversityStudent: z.enum(YES_NO).optional(),
    studying: z.string().trim().max(300).optional().default(""),
    studyLevel: z.enum(STUDY_LEVELS).optional(),
    hoursPerMonth: z.enum(MENTOR_HOURS).optional(),
    meetingFrequency: z.enum(MENTOR_FREQUENCY).optional(),
    mentorshipModel: z.enum(MENTOR_MODELS).optional(),
    menteePreference: z.enum(MENTEE_PREFERENCES).optional(),
    mentoredBefore: z.enum(YES_NO).optional(),
    guidanceTypes: z.array(z.enum(GUIDANCE_TYPES)).default([]),
    guidanceOther: z.string().trim().max(300).optional().default(""),
    technicalExpertise: z.string().trim().max(500).optional().default(""),
    feedbackSession: z.enum(YES_NO).optional(),

    // Closing (all tracks)
    howHeard: z.enum(HEAR_ABOUT_US, {
      errorMap: () => ({ message: "Please pick an option." }),
    }),
    howHeardOther: z.string().trim().max(200).optional().default(""),
    anythingElse: z.string().trim().max(2000).optional().default(""),
    newsletterOptIn: z.enum(YES_NO, {
      errorMap: () => ({ message: "Please select an option." }),
    }),

    // CV: required for advisor/both, optional for mentor-only
    cv: cvFileSchema.optional(),
  })
  .superRefine((d, ctx) => {
    const need = (path: string, message: string) =>
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: [path], message });
    const advisor = d.track === "advisor" || d.track === "both";
    const mentor = d.track === "mentor" || d.track === "both";

    if (advisor) {
      if (!d.ratingTechIndustry)
        need("ratingTechIndustry", "Please pick a rating from 1 to 5.");
      if (!d.ratingPalestinianEcosystem)
        need("ratingPalestinianEcosystem", "Please pick a rating from 1 to 5.");
      if (!d.experienceTypes?.length)
        need("experienceTypes", "Please select at least one option.");
      if (!d.cLevelExperience) need("cLevelExperience", "Please select an option.");
      if (!d.currentPosition?.trim())
        need("currentPosition", "Please enter your position.");
      if (!d.currentEmployer?.trim())
        need("currentEmployer", "Please enter your employer.");
      if (!d.yearsOfExperience) need("yearsOfExperience", "Please select an option.");
      if (!d.supportArea?.length) need("supportArea", "Please select at least one.");
      if (!d.workedWithGsgBefore)
        need("workedWithGsgBefore", "Please select an option.");
      if (!d.paidOrVolunteer) need("paidOrVolunteer", "Please select an option.");
      if (!d.engagementWays?.length)
        need("engagementWays", "Please select at least one.");
      if (!d.supportModes?.length)
        need("supportModes", "Please select at least one.");
      if (!d.cv) need("cv", "Please upload your CV.");
    }

    if (mentor) {
      if (!d.isUniversityStudent)
        need("isUniversityStudent", "Please select an option.");
      if (d.isUniversityStudent === "Yes") {
        if (!d.studying?.trim())
          need("studying", "Please tell us what you are studying.");
        if (!d.studyLevel) need("studyLevel", "Please select your study level.");
      }
      if (!d.hoursPerMonth) need("hoursPerMonth", "Please select an option.");
      if (!d.meetingFrequency) need("meetingFrequency", "Please select an option.");
      if (!d.mentorshipModel) need("mentorshipModel", "Please select an option.");
      if (!d.menteePreference) need("menteePreference", "Please select an option.");
      if (!d.mentoredBefore) need("mentoredBefore", "Please select an option.");
      if (!d.guidanceTypes?.length)
        need("guidanceTypes", "Please select at least one option.");
      if (
        d.guidanceTypes?.includes("Technical & Sector Expertise") &&
        !d.technicalExpertise?.trim()
      )
        need("technicalExpertise", "Please specify your area(s) of expertise.");
      if (d.guidanceTypes?.includes("Other") && !d.guidanceOther?.trim())
        need("guidanceOther", "Please describe the other type of guidance.");
      if (!d.feedbackSession) need("feedbackSession", "Please select an option.");
    }
  });

export type FormValues = z.input<typeof applicationSchema>;
export type FormValuesValidated = z.output<typeof applicationSchema>;
